import { type Task, Worker, type WorkerOptions } from './Worker';

export class TaskScheduler {
  private workers: Worker[] = [];
  private tasks: Task[] = [];
  private maxWorkersCount: number;
  private workerOptions?: WorkerOptions;

  constructor(maxWorkersCount: number, workerOptions?: WorkerOptions) {
    this.maxWorkersCount = maxWorkersCount;
    this.setNumberOfWorkers(maxWorkersCount, workerOptions);
  }

  private assignTasksToWorkers = () => {
    let task = this.getTaskToRun();
    let worker = this.getFreeWorker();

    while (task && worker) {
      worker.runTask(task);
      task = this.getTaskToRun();
      worker = this.getFreeWorker();
    }
  };

  public getTaskToRun = () =>
    this.tasks.find((task) => !task.hasFinished && !task.hasStarted && !task.hasError && !task.getIsBlocked?.());

  private getFreeWorker = () => {
    const busyWorkersCount = this.workers.filter((worker) => worker.getIsBusy()).length;

    if (busyWorkersCount >= this.maxWorkersCount) {
      return;
    }

    return this.workers.find((worker) => !worker.getIsBusy());
  };

  public setNumberOfWorkers = (numberOfWorkers: number, workerOptions?: WorkerOptions) => {
    this.maxWorkersCount = numberOfWorkers;
    this.workerOptions = workerOptions ?? this.workerOptions;

    while (this.workers.length < numberOfWorkers) {
      this.workers.push(
        new Worker(this.workers.length, {
          onTaskError: (taskId, error) => {
            this.workerOptions?.onTaskError?.(taskId, error);
            this.assignTasksToWorkers();
          },
          onTaskFinished: (taskId) => {
            this.workerOptions?.onTaskFinished?.(taskId);
            this.assignTasksToWorkers();
          },
          onTaskStarted: (taskId) => {
            this.workerOptions?.onTaskStarted?.(taskId);
            this.assignTasksToWorkers();
          },
        }),
      );
    }

    this.assignTasksToWorkers();
  };

  public getActiveWorkerCount = () => this.workers.filter((worker) => worker.getIsBusy()).length;

  /** Total worker pool size (busy + idle). The pool only ever grows, up to the high-water mark. */
  public getWorkerCount = () => this.workers.length;

  /** Number of tasks currently tracked by the scheduler (queued, running, or finished). */
  public getTaskCount = () => this.tasks.length;

  public addTasks(tasks: Task[]) {
    const newTaskIds = tasks.map(({ id }) => id);
    // remove tasks which already exist in our array to avoid duplication
    this.tasks = this.tasks.filter(({ id }) => !newTaskIds.includes(id));
    this.tasks.push(...tasks);
    this.assignTasksToWorkers();
  }

  public reset() {
    this.tasks = [];
    this.workers.forEach((worker) => {
      worker.resetWorker();
    });
  }
}
